import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { CreateDebtRequest, UpdateDebtRequest, Debt, Customer } from '../../types';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import { schedulePreview } from '../customer/CustomerPaymentPlans';
import { formatCurrency } from '../../utils/currency';

export type PlanDebtInput = Omit<CreateDebtRequest, 'customerId'> & { customerId?: string; installmentPlan?: { installmentCount: number; firstDueDate: string } };


interface DebtFormProps {
  debt?: Debt;
  onSubmit: (data: CreateDebtRequest | UpdateDebtRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isPayment?: boolean;
  prefilledCustomer?: Customer | null;
}

const getToday = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const getNowDateTime = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}T${hours}:${minutes}`;
};

const toDateTimeInput = (value?: string, fallbackDate?: string): string => {
  if (value) return value.slice(0, 16);
  return fallbackDate ? `${fallbackDate}T00:00` : getNowDateTime();
};

const DebtForm: React.FC<DebtFormProps> = ({
  debt,
  onSubmit,
  onCancel,
  isLoading = false,
  isPayment = false,
  prefilledCustomer,
}) => {
  const { t, language } = useLanguage();
  const isEditing = !!debt;
  const paymentMode = isPayment || (debt?.amount ?? 0) < 0;

  const [formData, setFormData] = useState({
    customerId: '',
    description: '',
    amount: '',
    transactionDate: getToday(),
    transactionDateTime: getNowDateTime(),
  });

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [planAvailable, setPlanAvailable] = useState(false);
  const [installmentPlan, setInstallmentPlan] = useState(false);
  const [installmentCount, setInstallmentCount] = useState('6');
  const [firstDueDate, setFirstDueDate] = useState(getToday());
  const preview = schedulePreview(formData.amount, installmentCount, firstDueDate);

  useEffect(() => {
    if (isEditing || paymentMode || !selectedCustomer || !apiClient.paymentPlans) { setPlanAvailable(false); setInstallmentPlan(false); return; }
    let active = true;
    apiClient.paymentPlans.list(selectedCustomer.id).then(() => { if (active) setPlanAvailable(true); })
      .catch(() => { if (active) { setPlanAvailable(false); setInstallmentPlan(false); } });
    return () => { active = false; };
  }, [isEditing, paymentMode, selectedCustomer]);

  // Load customers on component mount
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setCustomersLoading(true);
        const customerData = await apiClient.getCustomers();
        setCustomers(customerData);
        
        // If editing, find and set the selected customer
        if (debt) {
          const customer = customerData.find(c => c.id === debt.customerId);
          setSelectedCustomer(customer || null);
          setCustomerInputValue(customer ? `${customer.firstName} ${customer.lastName}` : '');
        } else if (prefilledCustomer) {
          // If we have a prefilled customer, set it
          setSelectedCustomer(prefilledCustomer);
          setCustomerInputValue(`${prefilledCustomer.firstName} ${prefilledCustomer.lastName}`);
        }
      } catch (error: unknown) {
        setErrors(prev => ({ ...prev, customerId: errorMessage(error, t('debts.loadError')) }));
      } finally {
        setCustomersLoading(false);
      }
    };

    loadCustomers();
  }, [debt, prefilledCustomer, t]);

  // Initialize form data when debt prop changes
  useEffect(() => {
    if (debt) {
      setFormData({
        customerId: debt.customerId,
        description: debt.description || '',
        amount: Math.abs(debt.amount).toString(),
        transactionDate: debt.transactionDate,
        transactionDateTime: toDateTimeInput(debt.transactionDateTime, debt.transactionDate),
      });
    } else {
      setFormData({
        customerId: prefilledCustomer?.id || '',
        description: '',
        amount: '',
        transactionDate: getToday(),
        transactionDateTime: getNowDateTime(),
      });
    }
  }, [debt, prefilledCustomer]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCustomerChange = (_event: React.SyntheticEvent, newValue: string | Customer | null) => {
    if (typeof newValue === 'string') {
      // User typed a new customer name
      setSelectedCustomer(null);
      setCustomerInputValue(newValue);
      setFormData(prev => ({ 
        ...prev, 
        customerId: '' 
      }));
    } else {
      // User selected an existing customer
      setSelectedCustomer(newValue);
      setCustomerInputValue(newValue ? `${newValue.firstName} ${newValue.lastName}` : '');
      setFormData(prev => ({ 
        ...prev, 
        customerId: newValue?.id || '' 
      }));
    }
    
    // Clear customer error
    if (errors.customerId) {
      setErrors(prev => ({ ...prev, customerId: '' }));
    }
  };

  const handleCustomerInputChange = (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    setCustomerInputValue(newInputValue);
    if (reason !== 'reset' && selectedCustomer && newInputValue !== `${selectedCustomer.firstName} ${selectedCustomer.lastName}`) {
      setSelectedCustomer(null);
      setFormData(prev => ({ ...prev, customerId: '' }));
    }
    if (errors.customerId) {
      setErrors(prev => ({ ...prev, customerId: '' }));
    }
  };



  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check if we have either a selected customer or a customer name for new customer creation
    if (!selectedCustomer && !customerInputValue.trim()) {
      newErrors.customerId = t('debts.customerRequired');
    }
    if (installmentPlan && (!selectedCustomer || preview.length === 0)) {
      newErrors.plan = language === 'tr' ? 'Müşteri, tutar ve geçerli bir taksit planı seçin.' : 'Select a customer and a valid installment schedule.';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = t('debts.amountRequired');
    } else {
      const amount = Number(formData.amount);
      if (!Number.isFinite(amount) || amount <= 0) {
        newErrors.amount = t('debts.amountInvalid');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      let amount = Number(formData.amount);
      
      // If this is a payment, make the amount negative
      if (paymentMode) {
        amount = -Math.abs(amount);
      }

      const submitData: PlanDebtInput = {
        customerId: selectedCustomer?.id || undefined, // Only set if customer is selected
        customerName: selectedCustomer ? undefined : customerInputValue.trim(), // For new customer creation
        description: formData.description.trim() || undefined,
        amount: amount,
        transactionDate: formData.transactionDate,
        transactionDateTime: formData.transactionDateTime.length === 16
          ? `${formData.transactionDateTime}:00`
          : formData.transactionDateTime,
        ...(installmentPlan && planAvailable ? { installmentPlan: { installmentCount: Number(installmentCount), firstDueDate } } : {}),
      };

      await onSubmit(submitData);
    } catch {
      // The parent dialog owns the visible mutation error.
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'grid', gap: 2.5, px: { xs: 2.5, sm: 3.5 }, pt: 3, pb: 3.5 }}>
        <Autocomplete
          options={customers}
          getOptionLabel={(option) => typeof option === 'string' ? option : `${option.firstName} ${option.lastName}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedCustomer}
          onChange={handleCustomerChange}
          inputValue={customerInputValue}
          onInputChange={handleCustomerInputChange}
          freeSolo
          loading={customersLoading}
          disabled={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('debts.customer')}
              error={!!errors.customerId}
              helperText={errors.customerId || (customerInputValue && !selectedCustomer ? t('debts.newCustomerWillBeCreated') : '')}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return <Box component="li" key={key} {...optionProps}>
              <Box>
                <Typography variant="body1">
                  {option.firstName} {option.lastName}
                </Typography>
                {option.email && (
                  <Typography variant="caption" color="text.secondary">
                    {option.email}
                  </Typography>
                )}
              </Box>
            </Box>;
          }}
        />

        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            fullWidth
            label={paymentMode ? t('debts.paymentAmount') : t('debts.amount')}
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleInputChange('amount')}
            error={!!errors.amount}
            helperText={errors.amount || (paymentMode ? t('debts.form.paymentAmountHint') : t('debts.form.debtAmountHint'))}
            required
            disabled={isLoading}
            inputProps={{ min: '0.01', step: '0.01', inputMode: 'decimal' }}
            InputProps={{
              startAdornment: <InputAdornment position="start">₺</InputAdornment>,
              sx: { fontSize: '1.25rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
            }}
            FormHelperTextProps={{ sx: { mx: 0, mt: 1.25, fontSize: '0.85rem' } }}
          />
        </Box>

        <TextField
          fullWidth
          label={t('debts.transactionDateTime')}
          name="transactionDateTime"
          type="datetime-local"
          value={formData.transactionDateTime}
          onChange={(event) => {
            const value = event.target.value;
            setFormData(prev => ({
              ...prev,
              transactionDateTime: value,
              transactionDate: value.slice(0, 10),
            }));
          }}
          disabled={isLoading}
          required
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          fullWidth
          label={t('debts.description')}
          name="description"
          value={formData.description}
          onChange={handleInputChange('description')}
          multiline
          minRows={2}
          maxRows={4}
          disabled={isLoading}
          placeholder={t('debts.descriptionPlaceholder')}
        />
        {planAvailable && <Box>
          <FormControlLabel control={<Checkbox checked={installmentPlan} onChange={e => setInstallmentPlan(e.target.checked)} disabled={isLoading} />}
            label={language === 'tr' ? 'Taksitli ödeme planı oluştur' : 'Create installment plan'} />
          {installmentPlan && <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField type="number" label={language === 'tr' ? 'Taksit sayısı' : 'Installment count'} value={installmentCount} onChange={e => setInstallmentCount(e.target.value)} inputProps={{ min: 1, max: 120 }} />
            <TextField type="date" label={language === 'tr' ? 'İlk vade' : 'First due date'} value={firstDueDate} onChange={e => setFirstDueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            {errors.plan && <Typography color="error">{errors.plan}</Typography>}
            <Box sx={{ maxHeight: 140, overflow: 'auto' }}>{preview.map((item, i) => <Typography variant="body2" key={i}>{i + 1}. {item.date} — {formatCurrency(item.cents / 100)}</Typography>)}</Box>
          </Box>}
        </Box>}
      </Box>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        gap: 1.5,
        justifyContent: 'flex-end',
        px: { xs: 2.5, sm: 3.5 },
        py: 2,
        mt: 'auto',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
      }}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={isLoading}
          sx={{ minWidth: { sm: 120 } }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          loading={isLoading}
          sx={{ minWidth: { sm: 164 } }}
        >
          {isEditing ? t('common.update') : paymentMode ? t('debts.form.savePayment') : t('debts.form.saveDebt')}
        </Button>
      </Box>
    </Box>
  );
};

export default DebtForm;
